
import { useState, useEffect } from 'react';
import { getClientIP, isIPInRange, parseIPRanges } from '@/utils/assessmentValidation';

// Global flag to track if initial IP check has been done (persists across component re-mounts)
let globalHasCheckedIP = false;
let globalClientIP = '';

export function useIPValidation(allowedIPRanges: string | null | undefined) {
  const [isValidIP, setIsValidIP] = useState<boolean | null>(null);
  const [clientIP, setClientIP] = useState<string>(globalClientIP);
  const [isChecking, setIsChecking] = useState(!globalHasCheckedIP);

  useEffect(() => {
    const checkIP = async () => {
      try {
        // Only show loading state on first check
        if (!globalHasCheckedIP) {
          setIsChecking(true);
        }

        const ip = await getClientIP();
        setClientIP(ip);
        globalClientIP = ip; // Store globally

        if (!allowedIPRanges) {
          setIsValidIP(true);
          return;
        }

        const ranges = parseIPRanges(allowedIPRanges);
        const isValid = isIPInRange(ip, ranges);
        setIsValidIP(isValid);
      } catch (error) {
        console.error('IP validation error:', error);
        setIsValidIP(true); // Allow access if IP check fails
      } finally {
        if (!globalHasCheckedIP) {
          setIsChecking(false);
          globalHasCheckedIP = true;
        }
      }
    };

    // Only run if we haven't checked yet globally
    if (!globalHasCheckedIP) {
      checkIP();
    } else {
      // If we've already checked globally, just use the stored values
      setClientIP(globalClientIP);
      setIsChecking(false);
      
      // Still validate the IP against current ranges
      if (!allowedIPRanges) {
        setIsValidIP(true);
      } else {
        const ranges = parseIPRanges(allowedIPRanges);
        const isValid = isIPInRange(globalClientIP, ranges);
        setIsValidIP(isValid);
      }
    }
  }, [allowedIPRanges]);

  return { isValidIP, clientIP, isChecking };
}
